import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Chip
} from '@mui/material';
import {
  SportsTennis as TennisIcon,
  Person as PersonIcon,
  PersonOutline as PersonOutlineIcon,
  Logout as LogoutIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isUserAdmin, isCoach } from '../utils/adminUtils';
import { getCoachByUid } from '../utils/firebaseUtils';

const Navigation = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrow = useMediaQuery(theme.breakpoints.down(901));
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile, logout, approvedCoachUids } = useAuth();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleProfilis = () => {
    handleMenuClose();
    navigate('/profilis');
  };

  const hasCoachPanel = currentUser && (getCoachByUid(currentUser.uid) || (approvedCoachUids && approvedCoachUids.includes(currentUser.uid)));
  const isUsersSection = location.pathname === '/admin/users' || location.pathname === '/admin/treneriai';
  const isRezervacijaBooking = location.pathname.startsWith('/rezervacija') && !location.pathname.startsWith('/rezervacija/grupines');
  const isRezervacijaSection = location.pathname === '/admin/visos-rezervacijos' || location.pathname === '/admin/treneriu-nuolatiniai-laikai' || isRezervacijaBooking;
  const isKalendoriusSection = location.pathname === '/admin' || location.pathname.startsWith('/admin/nuolatiniai-laikai');
  const menuItems = [
    { text: 'Rezervacija', path: '/rezervacija' },
    ...(isUserAdmin(currentUser)
      ? [
          { text: 'Admin', path: '/admin' },
          { text: 'Klientai', path: '/admin/users' },
          { text: 'Group', path: '/admin/grupines' }
        ]
      : hasCoachPanel
        ? [
            { text: 'Kalendorius', path: '/admin' },
            { text: 'Klientai', path: '/admin/coach-klientai' },
            { text: 'Visos rezervacijos', path: '/admin/visos-rezervacijos' }
          ]
        : [
            { text: 'Visos rezervacijos', path: '/visos-rezervacijos' }
          ])
  ];

  return (
    <>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #4a5568 0%, #718096 100%)',
          boxShadow: 2
        }}
      >
        <Container maxWidth="lg" disableGutters sx={{ px: { xs: 1, sm: 2 } }}>
          <Toolbar
            disableGutters
            sx={{
              justifyContent: 'space-between',
              minHeight: { xs: 48, sm: 56 },
              px: 0
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <Box
                component={Link}
                to="/"
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                <TennisIcon 
                  sx={{ 
                    mr: 1, 
                    fontSize: 32,
                    color: 'white'
                  }} 
                />
              </Box>
              <Typography
                variant="h6"
                component={Link}
                to="/"
                sx={{
                  textDecoration: 'none',
                  color: 'white',
                  fontWeight: 700,
                  [theme.breakpoints.down('sm')]: {
                    display: 'none'
                  }
                }}
              >
                TenisoUžsiėmimai.lt
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 2 }, alignItems: 'center', flexShrink: 0, minWidth: 0 }}>
              {/* Pagrindinis button - narrow screens (< 901px) */}
              {isNarrow && (
                <Button
                  component={Link}
                  to="/"
                  sx={{
                    color: 'white',
                    fontWeight: location.pathname === '/' ? 600 : 400,
                    borderBottom: location.pathname === '/' ? '2px solid white' : 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 1.5 },
                    minWidth: 0
                  }}
                >
                  Pagrindinis
                </Button>
              )}

              {/* Rezervacija: admin → visos rezervacijos; clients → booking page. Coaches without admin see Kalendorius instead */}
              {(isUserAdmin(currentUser) || !hasCoachPanel) && (
                <Button
                  component={Link}
                  to={isUserAdmin(currentUser) ? '/admin/visos-rezervacijos' : '/rezervacija'}
                  sx={{
                    color: 'white',
                    fontWeight: (isUserAdmin(currentUser) ? isRezervacijaSection : (location.pathname.startsWith('/rezervacija') && !location.pathname.startsWith('/rezervacija/grupines'))) ? 600 : 400,
                    borderBottom: (isUserAdmin(currentUser) ? isRezervacijaSection : (location.pathname.startsWith('/rezervacija') && !location.pathname.startsWith('/rezervacija/grupines'))) ? '2px solid white' : 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 1.5 },
                    minWidth: 0
                  }}
                >
                  Rezervacija
                </Button>
              )}
              {/* Kalendorius & Visos rezervacijos - narrow for coaches (not for admin-only) */}
              {isNarrow && hasCoachPanel && !isUserAdmin(currentUser) && (
                <>
                  <Button
                    component={Link}
                    to="/admin"
                    sx={{
                      color: 'white',
                      fontWeight: isKalendoriusSection ? 600 : 400,
                      borderBottom: isKalendoriusSection ? '2px solid white' : 'none',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      fontSize: '0.75rem',
                      px: 1,
                      minWidth: 0
                    }}
                  >
                    Kalendorius
                  </Button>
                  <Button
                    component={Link}
                    to="/admin/coach-klientai"
                    sx={{
                      color: 'white',
                      fontWeight: location.pathname === '/admin/coach-klientai' ? 600 : 400,
                      borderBottom: location.pathname === '/admin/coach-klientai' ? '2px solid white' : 'none',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      fontSize: '0.75rem',
                      px: 1,
                      minWidth: 0
                    }}
                  >
                    Klientai
                  </Button>
                  <Button
                    component={Link}
                    to="/admin/visos-rezervacijos"
                    sx={{
                      color: 'white',
                      fontWeight: location.pathname === '/admin/visos-rezervacijos' ? 600 : 400,
                      borderBottom: location.pathname === '/admin/visos-rezervacijos' ? '2px solid white' : 'none',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      fontSize: '0.75rem',
                      px: 1,
                      minWidth: 0
                    }}
                  >
                    Visos
                  </Button>
                </>
              )}
              {/* Grupinės treniruotės - only for clients (not admins or coaches) */}
              {!isUserAdmin(currentUser) && !isCoach(currentUser, approvedCoachUids) && (
                <Button
                  component={Link}
                  to="/rezervacija/grupines"
                  sx={{
                    color: 'white',
                    fontWeight: location.pathname === '/rezervacija/grupines' ? 600 : 400,
                    borderBottom: location.pathname === '/rezervacija/grupines' ? '2px solid white' : 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    px: { xs: 0.75, sm: 1.5 },
                    minWidth: 0
                  }}
                >
                  {isNarrow ? 'Grupinės' : 'Grupinės treniruotės'}
                </Button>
              )}

              {/* Visos rezervacijos - narrow for clients (not admins or coaches) */}
              {isNarrow && !isUserAdmin(currentUser) && !hasCoachPanel && (
                <Button
                  component={Link}
                  to="/visos-rezervacijos"
                  sx={{
                    color: 'white',
                    fontWeight: location.pathname === '/visos-rezervacijos' ? 600 : 400,
                    borderBottom: location.pathname === '/visos-rezervacijos' ? '2px solid white' : 'none',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    px: { xs: 0.75, sm: 1.5 },
                    minWidth: 0
                  }}
                >
                  Visos
                </Button>
              )}

              {/* Admin items - narrow with short labels */}
              {isNarrow && isUserAdmin(currentUser) && (
                <>
                  <Button
                    component={Link}
                    to="/admin"
                    sx={{
                      color: 'white',
                      fontWeight: location.pathname === '/admin' ? 600 : 400,
                      borderBottom: location.pathname === '/admin' ? '2px solid white' : 'none',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      fontSize: '0.75rem',
                      px: 1,
                      minWidth: 0
                    }}
                  >
                    Admin
                  </Button>
                  <Button
                    component={Link}
                    to="/admin/users"
                    sx={{
                      color: 'white',
                      fontWeight: isUsersSection ? 600 : 400,
                      borderBottom: isUsersSection ? '2px solid white' : 'none',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      fontSize: '0.75rem',
                      px: 1,
                      minWidth: 0
                    }}
                  >
                    Klientai
                  </Button>
                  <Button
                    component={Link}
                    to="/admin/grupines"
                    sx={{
                      color: 'white',
                      fontWeight: location.pathname.startsWith('/admin/grupines') ? 600 : 400,
                      borderBottom: location.pathname.startsWith('/admin/grupines') ? '2px solid white' : 'none',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      fontSize: '0.75rem',
                      px: 1,
                      minWidth: 0
                    }}
                  >
                    Group
                  </Button>
                </>
              )}

              {/* Other navigation items - wide screens only (full names) */}
              {!isNarrow && (
                <>
                  {menuItems.filter(item => item.path !== '/rezervacija').map((item) => (
                    <Button
                      key={item.text}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: 'white',
                        fontWeight: (location.pathname === item.path || (item.path === '/admin/grupines' && location.pathname.startsWith('/admin/grupines')) || (item.path === '/admin/users' && isUsersSection) || (item.path === '/admin' && isKalendoriusSection)) ? 600 : 400,
                        borderBottom: (location.pathname === item.path || (item.path === '/admin/grupines' && location.pathname.startsWith('/admin/grupines')) || (item.path === '/admin/users' && isUsersSection) || (item.path === '/admin' && isKalendoriusSection)) ? '2px solid white' : 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      {item.text}
                    </Button>
                  ))}
                </>
              )}
              
              {/* Authentication Section */}
              {currentUser ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
                  {currentUser.isAnonymous && (
                    <Chip
                      label="Anonimiškai"
                      size="small"
                      color="warning"
                      sx={{ color: 'white', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, height: { xs: 24, sm: 32 } }}
                    />
                  )}
                  {isUserAdmin(currentUser) && (
                    <Chip
                      label="Administratorius"
                      size="small"
                      color="success"
                      sx={{ color: 'white', fontSize: { xs: '0.7rem', sm: '0.8125rem' }, height: { xs: 24, sm: 32 } }}
                    />
                  )}
                  <IconButton
                    onClick={handleMenuOpen}
                    sx={{ color: 'white', p: { xs: 0.5, sm: 1 } }}
                    size="small"
                  >
                    <Avatar
                      src={userProfile?.photoURL || currentUser?.photoURL}
                      sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, bgcolor: 'primary.main' }}
                    >
                      {(userProfile?.fullName || currentUser?.displayName)?.[0]?.toUpperCase() ||
                        (currentUser.email ? currentUser.email[0].toUpperCase() : <PersonIcon />)}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem disabled>
                      {currentUser.email || 'Anoniminis vartotojas'}
                    </MenuItem>
                    {!currentUser.isAnonymous && (
                      <MenuItem onClick={handleProfilis}>
                        <PersonOutlineIcon sx={{ mr: 1 }} />
                        Profilis
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1 }} />
                      Atsijungti
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                <Button
                  component={Link}
                  to="/login"
                  variant="outlined"
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 1.5 }
                  }}
                  startIcon={<LoginIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />}
                >
                  Prisijungti
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Admin subsection bar: Admin (main) + Times most booked + When users book */}
      {isUserAdmin(currentUser) && (location.pathname === '/admin' || location.pathname === '/admin/administracija' || location.pathname === '/admin/statistika' || location.pathname === '/admin/when-users-book' || location.pathname === '/admin/aiksteliu-kainos') && (
        <Box
          sx={{
            bgcolor: 'grey.300',
            borderBottom: '1px solid',
            borderColor: 'grey.400',
            px: { xs: 1, sm: 2 },
            py: 0.5
          }}
        >
          <Container maxWidth="lg" disableGutters sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Button
              component={Link}
              to="/admin/administracija"
              size="small"
              sx={{
                color: (location.pathname === '/admin' || location.pathname === '/admin/administracija') ? 'white' : 'grey.800',
                fontWeight: (location.pathname === '/admin' || location.pathname === '/admin/administracija') ? 700 : 600,
                bgcolor: (location.pathname === '/admin' || location.pathname === '/admin/administracija') ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: (location.pathname === '/admin' || location.pathname === '/admin/administracija') ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Admin
            </Button>
            <Button
              component={Link}
              to="/admin/statistika"
              size="small"
              sx={{
                color: location.pathname === '/admin/statistika' ? 'white' : 'grey.800',
                fontWeight: location.pathname === '/admin/statistika' ? 700 : 600,
                bgcolor: location.pathname === '/admin/statistika' ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname === '/admin/statistika' ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Times most booked
            </Button>
            <Button
              component={Link}
              to="/admin/when-users-book"
              size="small"
              sx={{
                color: location.pathname === '/admin/when-users-book' ? 'white' : 'grey.800',
                fontWeight: location.pathname === '/admin/when-users-book' ? 700 : 600,
                bgcolor: location.pathname === '/admin/when-users-book' ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname === '/admin/when-users-book' ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              When users book
            </Button>
            <Button
              component={Link}
              to="/admin/aiksteliu-kainos"
              size="small"
              sx={{
                color: location.pathname === '/admin/aiksteliu-kainos' ? 'white' : 'grey.800',
                fontWeight: location.pathname === '/admin/aiksteliu-kainos' ? 700 : 600,
                bgcolor: location.pathname === '/admin/aiksteliu-kainos' ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname === '/admin/aiksteliu-kainos' ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Aikštelių kainos
            </Button>
          </Container>
        </Box>
      )}

      {/* Rezervacija subsection bar (admin): Visos rezervacijos + Rezervuoti trenerį */}
      {isUserAdmin(currentUser) && (location.pathname === '/admin/visos-rezervacijos' || location.pathname === '/admin/treneriu-nuolatiniai-laikai' || isRezervacijaBooking) && (
        <Box
          sx={{
            bgcolor: 'grey.300',
            borderBottom: '1px solid',
            borderColor: 'grey.400',
            px: { xs: 1, sm: 2 },
            py: 0.5
          }}
        >
          <Container maxWidth="lg" disableGutters sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Button
              component={Link}
              to="/admin/visos-rezervacijos"
              size="small"
              sx={{
                color: location.pathname === '/admin/visos-rezervacijos' ? 'white' : 'grey.800',
                fontWeight: location.pathname === '/admin/visos-rezervacijos' ? 700 : 600,
                bgcolor: location.pathname === '/admin/visos-rezervacijos' ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname === '/admin/visos-rezervacijos' ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Visos rezervacijos
            </Button>
            <Button
              component={Link}
              to="/rezervacija"
              size="small"
              sx={{
                color: isRezervacijaBooking ? 'white' : 'grey.800',
                fontWeight: isRezervacijaBooking ? 700 : 600,
                bgcolor: isRezervacijaBooking ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: isRezervacijaBooking ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Rezervuoti trenerį
            </Button>
            <Button
              component={Link}
              to="/admin/treneriu-nuolatiniai-laikai"
              size="small"
              sx={{
                color: location.pathname === '/admin/treneriu-nuolatiniai-laikai' ? 'white' : 'grey.800',
                fontWeight: location.pathname === '/admin/treneriu-nuolatiniai-laikai' ? 700 : 600,
                bgcolor: location.pathname === '/admin/treneriu-nuolatiniai-laikai' ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname === '/admin/treneriu-nuolatiniai-laikai' ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Trenerių nuolatiniai laikai
            </Button>
          </Container>
        </Box>
      )}

      {/* Kalendorius subsection bar (coach): Kalendorius + Nuolatiniai laisvi laikai */}
      {hasCoachPanel && !isUserAdmin(currentUser) && (location.pathname === '/admin' || location.pathname.startsWith('/admin/nuolatiniai-laikai')) && (
        <Box
          sx={{
            bgcolor: 'grey.300',
            borderBottom: '1px solid',
            borderColor: 'grey.400',
            px: { xs: 1, sm: 2 },
            py: 0.5
          }}
        >
          <Container maxWidth="lg" disableGutters sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Button
              component={Link}
              to="/admin"
              size="small"
              sx={{
                color: location.pathname === '/admin' ? 'white' : 'grey.800',
                fontWeight: location.pathname === '/admin' ? 700 : 600,
                bgcolor: location.pathname === '/admin' ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname === '/admin' ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Kalendorius
            </Button>
            <Button
              component={Link}
              to="/admin/nuolatiniai-laikai"
              size="small"
              sx={{
                color: location.pathname.startsWith('/admin/nuolatiniai-laikai') ? 'white' : 'grey.800',
                fontWeight: location.pathname.startsWith('/admin/nuolatiniai-laikai') ? 700 : 600,
                bgcolor: location.pathname.startsWith('/admin/nuolatiniai-laikai') ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname.startsWith('/admin/nuolatiniai-laikai') ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Nuolatiniai laisvi laikai
            </Button>
          </Container>
        </Box>
      )}

      {/* Users section subsection bar: Klientai + Treneriai */}
      {(location.pathname === '/admin/users' || location.pathname === '/admin/treneriai') && (
        <Box
          sx={{
            bgcolor: 'grey.300',
            borderBottom: '1px solid',
            borderColor: 'grey.400',
            px: { xs: 1, sm: 2 },
            py: 0.5
          }}
        >
          <Container maxWidth="lg" disableGutters sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Button
              component={Link}
              to="/admin/users"
              size="small"
              sx={{
                color: location.pathname === '/admin/users' ? 'white' : 'grey.800',
                fontWeight: location.pathname === '/admin/users' ? 700 : 600,
                bgcolor: location.pathname === '/admin/users' ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname === '/admin/users' ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Klientai
            </Button>
            <Button
              component={Link}
              to="/admin/treneriai"
              size="small"
              sx={{
                color: location.pathname === '/admin/treneriai' ? 'white' : 'grey.800',
                fontWeight: location.pathname === '/admin/treneriai' ? 700 : 600,
                bgcolor: location.pathname === '/admin/treneriai' ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname === '/admin/treneriai' ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Treneriai
            </Button>
          </Container>
        </Box>
      )}

      {/* Group subsection bar: extends nav with lighter grey when on Group section */}
      {location.pathname.startsWith('/admin/grupines') && (
        <Box
          sx={{
            bgcolor: 'grey.300',
            borderBottom: '1px solid',
            borderColor: 'grey.400',
            px: { xs: 1, sm: 2 },
            py: 0.5
          }}
        >
          <Container maxWidth="lg" disableGutters sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Button
              component={Link}
              to="/admin/grupines"
              size="small"
              sx={{
                color: location.pathname === '/admin/grupines' ? 'white' : 'grey.800',
                fontWeight: location.pathname === '/admin/grupines' ? 700 : 600,
                bgcolor: location.pathname === '/admin/grupines' ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname === '/admin/grupines' ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Klientų pageidavimai
            </Button>
            <Button
              component={Link}
              to="/admin/grupines/formavimas"
              size="small"
              sx={{
                color: location.pathname === '/admin/grupines/formavimas' ? 'white' : 'grey.800',
                fontWeight: location.pathname === '/admin/grupines/formavimas' ? 700 : 600,
                bgcolor: location.pathname === '/admin/grupines/formavimas' ? 'grey.600' : 'transparent',
                '&:hover': { bgcolor: location.pathname === '/admin/grupines/formavimas' ? 'grey.600' : 'grey.400', color: 'white' }
              }}
            >
              Grupių formavimas
            </Button>
          </Container>
        </Box>
      )}

    </>
  );
};

export default Navigation; 